import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nav } from '@/components/Nav';
import { Footer } from '@/components/Footer';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Calendar, MapPin, Users, Car, Plus, Loader2 } from 'lucide-react';

const statusLabels = {
    pending: { label: 'En attente', color: 'text-yellow-400' },
    confirmed: { label: 'Confirmée', color: 'text-green-400' },
    completed: { label: 'Terminée', color: 'text-[#A3A39D]' },
    cancelled: { label: 'Annulée', color: 'text-red-400' },
};

export default function Dashboard() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate('/'); return; }
        (async () => {
            try {
                const { data } = await api.get('/bookings');
                setBookings(data);
            } catch {}
            finally { setLoading(false); }
        })();
    }, [user, authLoading, navigate]);

    if (authLoading || !user) {
        return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A059]" /></div>;
    }

    return (
        <div className="bg-[#050505] min-h-screen text-[#F8F8F5]">
            <Nav />
            <div className="max-w-6xl mx-auto px-6 md:px-12 pt-32 pb-20">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">Mon compte</div>
                        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight">Bonjour, <em className="text-[#C5A059] not-italic">{user.name?.split(' ')[0]}</em></h1>
                        <p className="text-[#A3A39D] mt-2 text-sm">{user.email}</p>
                    </div>
                    <button data-testid="dashboard-new-booking-btn" onClick={() => navigate('/reservation')}
                        className="bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.25em] flex items-center gap-2 hover:bg-[#DCC283]">
                        <Plus size={14} /> Nouvelle réservation
                    </button>
                </div>

                <div className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-6">Mes réservations</div>
                {loading ? (
                    <Loader2 className="animate-spin text-[#C5A059]" />
                ) : bookings.length === 0 ? (
                    <div data-testid="dashboard-empty" className="border border-[#222] p-12 text-center">
                        <p className="text-[#A3A39D]">Vous n'avez pas encore de réservation.</p>
                        <button onClick={() => navigate('/reservation')} className="mt-6 text-[#C5A059] text-xs uppercase tracking-[0.25em] hover:text-[#DCC283]">
                            Effectuer ma première réservation →
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {bookings.map(b => {
                            const st = statusLabels[b.status] || statusLabels.pending;
                            return (
                                <div key={b.booking_id} data-testid={`booking-${b.booking_id}`}
                                    className="border border-[#222] hover:border-[#C5A059]/40 p-6 md:p-8 grid grid-cols-1 md:grid-cols-5 gap-4 transition-colors">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A39D]">Ref.</div>
                                        <div className="font-mono text-[#F8F8F5] mt-1 text-sm">{b.booking_id.slice(0, 10).toUpperCase()}</div>
                                        <div className={`text-xs uppercase tracking-[0.2em] mt-2 ${st.color}`}>{st.label}</div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A39D]"><Calendar size={12} /> Date</div>
                                        <div className="text-sm mt-2">{b.pickup_date} <span className="text-[#A3A39D]">à</span> {b.pickup_time}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A39D]"><MapPin size={12} /> Itinéraire</div>
                                        <div className="text-sm mt-2 truncate">{b.pickup_city ? `${b.pickup_city} · ` : ''}{b.pickup_address}</div>
                                        <div className="text-sm text-[#A3A39D] truncate">→ {b.dropoff_city ? `${b.dropoff_city} · ` : ''}{b.dropoff_address}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center justify-end gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A39D]"><Car size={12} /> {b.vehicle_type}</div>
                                        <div className="text-xs text-[#A3A39D] mt-1 flex items-center justify-end gap-2"><Users size={12} /> {b.passengers} pax</div>
                                        <div className="font-display text-2xl text-[#C5A059] mt-3">{b.estimated_price?.toFixed(2)} €</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}
